/* eslint-disable sonarjs/cognitive-complexity, react/forbid-component-props, @next/next/no-img-element */
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  ADDON_EXTENSION,
  POST_CHECKOUT_SOURCE,
} from '../../config/constants/addon-types';
import { GREEN_TICK } from '../../config/constants/images';
import {
  EXTEND_DROP_DOWN_SUB_TITLE,
  EXTEND_DROP_DOWN_TITLE,
  PROGRESS_MESSAGE_SUCCESS,
  VOUCHER_EXTENDED,
} from '../../config/text/text';
import addonExpressBuyService from '../../helpers/addonExpressBuyService';
import { trackEvent } from '../../helpers/analytics';
import useCheckMobile from '../../helpers/checkMobile';
import ThemeContext from '../../providers/ThemeProvider';
import { VoucherArrow } from '../VoucherItem/VoucherArrow';
import WowModal from '../WowModal';
import PrimaryButton from '../_generic/button/PrimaryButton';
import Spinner from '../_generic/spinner/Spinner';
import PaymentModal from '../checkout/payment/PaymentModal';
import ValidityDropDownItem from './ValidityDropDownItem';

const ValidityDropDown = ({ extensions, mutate, paymentInfo }) => {
  const theme = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const chevronIcon = isOpen ? faCaretUp : faCaretDown;
  const container = useRef(null);
  const [paymentInstruments, setPaymentInstruments] = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [showArrow, setShowArrow] = useState(false);
  const [canBuyNow, setCanBuyNow] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showTick, setShowTick] = useState(false);
  const [modalBackdrop, setModalBackdrop] = useState('static');
  const [userCanCloseModal, setUserCanCloseModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [failedExpressBuy, setFailedExpressBuy] = useState(false);
  const ref = useRef(null);
  const router = useRouter();

  useEffect(() => {
    if (!router || !router.query) return;

    const { ito } = router.query;

    if (
      ito &&
      (ito.startsWith('wowcher_email_mg_order-extension-reminder_') ||
        ito.startsWith('livingsocial_email_mg_order-extension-reminder_'))
    ) {
      setShowArrow(true);
      setIsOpen(true);
    }
  }, [router?.query]);

  const handleButtonClick = () => {
    trackEvent(`extend_validity_dd`);
    setIsOpen(!isOpen);
    setShowArrow(false);
  };

  const orderFailedHandler = (isPaymentModal = false) => {
    let timeout = 5_000;
    if (!isPaymentModal) {
      // no messages on express buy failure, open payment modal quickly
      setShowMessageModal(false);
      timeout = 500;
    }
    setUserCanCloseModal(true); // add cross to modal
    setModalBackdrop(true); // can click bg to close
    setShowSpinner(false);
    setShowTick(false);

    setTimeout(() => {
      // close message after 5 seconds and show payment modal
      setShowMessageModal(false);
      if (!isPaymentModal) {
        if (addonExpressBuyService.failedPayment) setFailedExpressBuy(true);
        setShowPaymentModal(true);
      }
    }, timeout);
  };

  const orderCompleteHandler = (isPaymentModal = false) => {
    setShowSpinner(false);
    setShowTick(true);
    setUserCanCloseModal(true); // add cross to modal

    if (isPaymentModal) {
      setProgressMessage(PROGRESS_MESSAGE_SUCCESS);
      setShowPaymentModal(false);
      setShowMessageModal(true);
    }
    setTimeout(() => {
      // after 5 seconds close modal and reload vouchers
      setShowMessageModal(false);
      setShowTick(false);
      mutate();
    }, 5_000);
  };

  const expressBuyMessage = (message) => {
    setProgressMessage(message);
  };

  const handleBuyButtonClick = (extension) => {
    extension.addonType = ADDON_EXTENSION;
    extension.source = POST_CHECKOUT_SOURCE;
    setSelectedExtension(extension);
    if (canBuyNow) {
      setUserCanCloseModal(false);
      setShowMessageModal(true);
      setShowSpinner(true);
      setModalBackdrop('static'); // can't click bg to close
      addonExpressBuyService.failure = orderFailedHandler;
      addonExpressBuyService.success = orderCompleteHandler;
      addonExpressBuyService.setProgressMessage = (message) =>
        expressBuyMessage(message);
      addonExpressBuyService.paymentInfo = paymentInfo;
      addonExpressBuyService.productOrderId = extension.productOrderId;
      addonExpressBuyService.deviceData = deviceData;
      // Toggle value of sendEmail based on from where it is called
      addonExpressBuyService.sendEmail = true;
      addonExpressBuyService.initExpressBuy([extension]);
    } else {
      setFailedExpressBuy(false);
      setShowPaymentModal(true);
    }
  };

  useEffect(() => {
    if (paymentInfo && paymentInfo.paymentInstruments) {
      setPaymentInstruments(paymentInfo.paymentInstruments);
      setDeviceData(paymentInfo.paymentDeviceProfileVO.sessionId);
    }
    if (paymentInfo && paymentInfo.buyNow) {
      setCanBuyNow(paymentInfo.buyNow);
    }
  }, [paymentInfo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // don't close when behind modal
      if (showMessageModal) return;
      if (!container.current || !container.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      if (isOpen) trackEvent(`bounce`);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showMessageModal]);
  const isMobile = useCheckMobile(false);
  if (!extensions || (extensions && extensions.length === 0)) return null;

  return (
    <>
      <div className="validity-dd-wrapper">
        {showArrow && (
          <div className="vve-arrow">
            <VoucherArrow />
          </div>
        )}
        <div
          className={classNames('validity-dd', {
            'expander-open': isOpen,
          })}
          data-testid="validity-dd"
          ref={container}
        >
          <PrimaryButton
            className="full-width align-left less-rounded"
            id="extend-validity-button"
            onClick={handleButtonClick}
          >
            {EXTEND_DROP_DOWN_TITLE}
            <div className="icon">
              <FontAwesomeIcon
                color={theme.commoncolors.white}
                icon={chevronIcon}
              />
            </div>
          </PrimaryButton>
          <div className="expander">
            <p className="sub-title">{EXTEND_DROP_DOWN_SUB_TITLE}</p>
            {extensions.map(function (extension) {
              return (
                <ValidityDropDownItem
                  canBuyNow={canBuyNow}
                  extension={extension}
                  handleBuyButtonClick={handleBuyButtonClick}
                  key={`extension-${extension.orderLineId}`}
                />
              );
            })}
          </div>
          {showPaymentModal && (
            <PaymentModal
              currentRef={ref.current}
              deviceData={deviceData}
              failedExpressBuy={failedExpressBuy}
              isAuthCheckFinished
              isAuthenticated
              orderCompleteHandler={orderCompleteHandler}
              orderFailedHandler={orderFailedHandler}
              paymentInstruments={paymentInstruments}
              selectedAddons={[selectedExtension]}
              setShowPaymentModal={setShowPaymentModal}
              showPaymentModal={showPaymentModal}
            />
          )}
          <WowModal
            ariaLabelledBy="payment-progress"
            backdrop={modalBackdrop}
            centered
            setShowModal={setShowMessageModal}
            showCloseButton={userCanCloseModal}
            showHeader
            showModal={showMessageModal}
          >
            <div>
              <p className="modal-message">{progressMessage}</p>
              {showTick && (
                <p className="modal-message sub-message">{VOUCHER_EXTENDED}</p>
              )}
              {showTick && (
                <div className="tick-container">
                  <img
                    alt="greet tick"
                    className="tick-icon"
                    src={GREEN_TICK}
                  />
                </div>
              )}
              {showSpinner && (
                <Spinner display="relative" hasMsg={false} noMargin />
              )}
            </div>
          </WowModal>
        </div>
      </div>
      <style jsx>{`
        .validity-dd-wrapper {
          position: relative;
        }
        .vve-arrow {
          position: absolute;
          right: 0;
          transform: ${isMobile
            ? 'translate(-450%, -35%)'
            : 'translate(-660%, 0%)'};
          z-index: 1;
        }
        .validity-dd {
          display: block;
          padding: 10px 0;
          position: sticky;
          top: 0;
          z-index: 1;
        }
        .icon {
          margin-left: auto;
        }
        .tick-container {
          padding: 30px 0;
          text-align: center;
        }
        .tick-icon {
          height: 100px;
        }
        .expander {
          background-color: white;
          box-shadow: ${theme.colors.dropshadow} 0 3px 6px;
          display: none;
          margin-top: -20px;
          max-height: 380px;
          min-height: 100px;
          overflow: auto;
          padding: 30px 10px 5px 10px;
          position: absolute;
          width: 100%;
          z-index: -1;
        }
        .expander-open .expander {
          display: block;
        }
        .sub-title {
          border-bottom: 1px solid ${theme.commoncolors.greyshade};
          padding-bottom: 5px;
          font-size: 13px;
        }
        .spinner-container {
          display: block;
          height: 160px;
          position: relative;
        }
        .modal-message {
          color: ${showTick
            ? theme.commoncolors.successcolor
            : theme.commoncolors.black};
          font-size: ${showTick ? '3rem' : '1.8rem'};
          text-align: center;
        }
        .sub-message {
          font-size: 2rem;
        }

        @media only screen and (min-width: ${theme.breakpoints.smUp}) {
          .validity-dd {
            width: 360px;
          }
        }
        @media only screen and (min-width: ${theme.breakpoints.mdUp}) {
          .validity-dd {
            padding-top: 0;
          }
        }
      `}</style>
    </>
  );
};

ValidityDropDown.propTypes = {
  extensions: PropTypes.array,
  mutate: PropTypes.func,
  paymentInfo: PropTypes.object,
};

ValidityDropDown.defaultProps = {
  extensions: null,
  paymentInfo: null,
};

export default ValidityDropDown;
